// Setzt ein Method-Scaffold (Block + Regions) atomar in die Y.Map "objects" ein.
// Aufrufer uebersetzen i18n-Keys vorab.
//
// V3 Slice 5: Statt naivem (40,40)-Offset wird die Method-Box gegen ALLE Objekte
// auf dem Board AABB-getestet (mit Padding). Wenn der Viewport-Center belegt ist,
// sucht eine Spirale in 8 Richtungen den nächsten freien Slot. Caller bekommt den
// effektiven Center zurueck und kann den Viewport dorthin pannen.

import {
  type CanvasObject,
  type DependencyObj,
  type IconObj,
  METHOD_CATALOG,
  type MethodId,
  type PainObj,
  type RegionObj,
  type RequirementObj,
  type ShapeObj,
  buildMethodScaffold,
  isCanvasObject,
  methodBlockSize,
} from "@ms-whiteboard/shared";
import type * as Y from "yjs";
import { objectBounds } from "../canvas/object-bounds.js";
import {
  findFreeSlotWithAutoExpand,
  findRegionForHintInBlock,
} from "../canvas/region-routing.js";

const SEARCH_STEP_PX = 80;
const MAX_SEARCH_RADIUS_PX = 2400;
// Generoeser Abstand zwischen zwei Methoden-Blocks — sonst klebt der zweite
// Block direkt an der Border des ersten und es laesst sich kaum klicken.
const COLLISION_PADDING_PX = 80;

export interface InsertMethodOptions {
  methodId: MethodId;
  yMap: Y.Map<unknown>;
  centerX: number;
  centerY: number;
  createdBy: string;
  /** Pre-translated labels [block, region1, region2, ...]. */
  labels: readonly string[];
  newId: () => string;
}

export interface InsertMethodResult {
  blockId: string;
  /** Effektiver Welt-Mittelpunkt des eingefuegten Method-Blocks. Caller pannt dorthin. */
  center: { x: number; y: number };
  /** Anzahl freier Requirements, die beim Insert in passende Quadranten einsortiert wurden. */
  autoRoutedCount: number;
}

interface Box {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export function insertMethodScaffold(opts: InsertMethodOptions): InsertMethodResult {
  const bbox = methodBlockSize(opts.methodId);
  const obstacles = collectObjectBoxes(opts.yMap);
  const center = findFreeCenter(opts.centerX, opts.centerY, bbox, obstacles);

  const objects = buildMethodScaffold({
    methodId: opts.methodId,
    centerX: center.x,
    centerY: center.y,
    createdBy: opts.createdBy,
    createdAt: Date.now(),
    newId: opts.newId,
    labels: opts.labels,
  });
  const blockId = objects[0]?.id ?? "";
  let autoRoutedCount = 0;
  opts.yMap.doc?.transact(() => {
    for (const obj of objects) opts.yMap.set(obj.id, obj);
    // Initial-Pass: freie semantische Objekte (requirement/pain/dependency) in die
    // passenden semanticHint-Regionen des neuen Blocks verteilen. Hint-Feld pro Typ:
    // requirement→priority, pain/dependency→severity. accepts-Filter verhindert,
    // dass eine Dependency in eine Pain-Region rutscht (gleicher severity-Hint).
    // Snapshot vor dem Move-Loop — sonst wird die yMap-Iteration mitten in der Mutation undefiniert.
    const freeSemantics: Array<RequirementObj | PainObj | DependencyObj> = [];
    for (const value of opts.yMap.values()) {
      if (!isCanvasObject(value)) continue;
      if (value.parentRegionId !== null) continue;
      if (value.type === "requirement" || value.type === "pain" || value.type === "dependency") {
        freeSemantics.push(value);
      }
    }
    for (const sem of freeSemantics) {
      const hintField = sem.type === "requirement" ? "priority" : "severity";
      const hintValue = sem.type === "requirement" ? sem.priority : sem.severity;
      if (!hintValue) continue; // dependency.severity ist optional
      const target = findRegionForHintInBlock(opts.yMap, blockId, hintField, hintValue, sem.type);
      if (!target) continue;
      const b = objectBounds(sem);
      const slot = findFreeSlotWithAutoExpand(opts.yMap, target, { width: b.w, height: b.h });
      opts.yMap.set(sem.id, {
        ...sem,
        x: slot.x,
        y: slot.y,
        parentRegionId: target.id,
      });
      autoRoutedCount += 1;
    }
    // Welle 3: context-target-light bekommt Default-Symbole je Region (Akteure/Systeme/
    // Daten/Schnittstellen) + ein Zielsystem-Hexagon ins Zentrum als lesbares Startbild.
    if (opts.methodId === "context-target-light") {
      seedContextTargetContent(
        opts.yMap,
        objects,
        opts.createdBy,
        opts.newId,
        opts.labels[1] ?? "",
      );
    }
  });
  return { blockId, center, autoRoutedCount };
}

/** Region-Default-Inhalte fuer context-target-light. Region-Reihenfolge des Scaffolds:
 *  [0]=Zentrum, [1]=Akteure, [2]=Systeme, [3]=Daten, [4]=Schnittstellen. */
function seedContextTargetContent(
  yMap: Y.Map<unknown>,
  scaffold: readonly CanvasObject[],
  createdBy: string,
  newId: () => string,
  targetLabel: string,
): void {
  const regions = scaffold.filter((o): o is RegionObj => o.type === "region");
  const [center, actors, systems, data, interfaces] = regions;
  const now = Date.now();
  const centered = (region: RegionObj, w: number, h: number) => ({
    x: region.x + region.width / 2 - w / 2,
    y: region.y + region.height / 2 - h / 2,
  });

  if (center) {
    const w = 180;
    const h = 90;
    const pos = centered(center, w, h);
    const hexagon: ShapeObj = {
      id: newId(),
      type: "shape",
      x: pos.x,
      y: pos.y,
      width: w,
      height: h,
      kind: "hexagon",
      text: targetLabel,
      accent: "#ea5b0b",
      createdBy,
      createdAt: now,
      parentRegionId: center.id,
    };
    yMap.set(hexagon.id, hexagon);
  }

  const iconSeeds: ReadonlyArray<[RegionObj | undefined, string]> = [
    [actors, "User"],
    [systems, "Server"],
    [data, "Database"],
    [interfaces, "Plug"],
  ];
  iconSeeds.forEach(([region, iconName], idx) => {
    if (!region) return;
    const size = 56;
    const pos = centered(region, size, size);
    const icon: IconObj = {
      id: newId(),
      type: "icon",
      x: pos.x,
      y: pos.y,
      width: size,
      height: size,
      iconName,
      createdBy,
      createdAt: now + idx + 1,
      parentRegionId: region.id,
    };
    yMap.set(icon.id, icon);
  });
}

function collectObjectBoxes(yMap: Y.Map<unknown>): Box[] {
  const boxes: Box[] = [];
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    const b = objectBounds(value);
    boxes.push({ x: value.x, y: value.y, width: b.w, height: b.h });
  }
  return boxes;
}

function rectIntersects(a: Box, b: Box, pad: number): boolean {
  return !(
    a.x + a.width + pad <= b.x ||
    a.x >= b.x + b.width + pad ||
    a.y + a.height + pad <= b.y ||
    a.y >= b.y + b.height + pad
  );
}

function isFree(
  centerX: number,
  centerY: number,
  bbox: { width: number; height: number },
  obstacles: readonly Box[],
): boolean {
  const candidate: Box = {
    x: centerX - bbox.width / 2,
    y: centerY - bbox.height / 2,
    width: bbox.width,
    height: bbox.height,
  };
  for (const ob of obstacles) {
    if (rectIntersects(candidate, ob, COLLISION_PADDING_PX)) return false;
  }
  return true;
}

const SPIRAL_DIRS: ReadonlyArray<readonly [number, number]> = [
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
];

function findFreeCenter(
  centerX: number,
  centerY: number,
  bbox: { width: number; height: number },
  obstacles: readonly Box[],
): { x: number; y: number } {
  if (isFree(centerX, centerY, bbox, obstacles)) return { x: centerX, y: centerY };
  for (let r = SEARCH_STEP_PX; r <= MAX_SEARCH_RADIUS_PX; r += SEARCH_STEP_PX) {
    for (const [dx, dy] of SPIRAL_DIRS) {
      const cx = centerX + dx * r;
      const cy = centerY + dy * r;
      if (isFree(cx, cy, bbox, obstacles)) return { x: cx, y: cy };
    }
  }
  // Fallback — selbst nach 2400 px kein freier Slot. Wir setzen ihn am Origin-Center;
  // der User darf den Block dann manuell schieben.
  return { x: centerX, y: centerY };
}

export function methodLabelKeys(methodId: MethodId): readonly string[] {
  const def = METHOD_CATALOG.find((m) => m.id === methodId);
  if (!def) return [];
  return [def.nameKey, ...def.regionLabelKeys];
}

export function newCanvasId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
